// @generated by liquid-inject from src/assets/algolia-header-panel.ts
// src/assets/algolia-header-panel.ts
import {
  ALGOLIA_HIDDEN_GUARD_CONTEXT,
  ALGOLIA_HIDDEN_PRODUCTS_FILTER,
  CDN_ALGOLIASEARCH,
  CDN_AUTOCOMPLETE_CORE,
  EXPLORE_PUSH_KEY,
  itemUrl as itemUrl3,
  loadScript,
  lookupRedirect,
  minimumQueryLength,
  parseShortQueries,
  productAttributesToRetrieve,
  readPushHandles,
  readRedirectUrl,
  readStorefrontConfig,
  REDIRECT_LOOKUP_TIMEOUT_MS,
  resolveExplorePushes,
  giftedVersion,
  refreshGiftedHandles,
  resolveGiftsForHits,
  safeRedirectUrl,
  SEARCH_DEBOUNCE_MS,
  searchUrlFor as searchUrlFor3,
  SRI_ALGOLIASEARCH,
  SRI_AUTOCOMPLETE_CORE,
  withTimeout
} from "@theme/algolia-shared";

// src/shared/utils/section-render.ts
var SECTION_PREFIX = "shopify-section-";
function getSectionId(el) {
  if (!el?.id?.startsWith(SECTION_PREFIX)) return null;
  return el.id.slice(SECTION_PREFIX.length);
}
function closestSectionId(el) {
  const wrapper = el?.closest(`[id^="${SECTION_PREFIX}"]`);
  return getSectionId(wrapper);
}
function collectSectionIds(elements) {
  const ids = /* @__PURE__ */ new Set();
  for (const el of elements) {
    const id = closestSectionId(el);
    if (id) ids.add(id);
  }
  return [...ids];
}
function buildUrl(base, params) {
  const url = new URL(base ?? window.location.pathname, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }
  return url.toString();
}
function withQuery(params, query) {
  if (!query) return params;
  const extra = Object.fromEntries(
    Object.entries(query).map(([k, v]) => [k, String(v)])
  );
  return { ...extra, ...params };
}
async function fetchSections(ids, options = {}) {
  const url = buildUrl(
    options.url,
    withQuery({ sections: ids.join(",") }, options.query)
  );
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) throw new Error(`Sections fetch ${response.status}`);
  return response.json();
}
async function fetchSectionHTML(id, options = {}) {
  const url = buildUrl(
    options.url,
    withQuery({ section_id: id }, options.query)
  );
  const response = await fetch(url, { credentials: "same-origin" });
  if (!response.ok) throw new Error(`Section fetch ${response.status}`);
  return response.text();
}
function swapInner(html, selector) {
  const selectors = Array.isArray(selector) ? selector : [selector];
  const doc = new DOMParser().parseFromString(html, "text/html");
  selectors.forEach((sel) => {
    document.querySelectorAll(sel).forEach((element) => {
      const source = element.dataset.id ? doc.querySelector(`${sel}[data-id="${element.dataset.id}"]`) : doc.querySelector(sel);
      if (source) element.innerHTML = source.innerHTML;
    });
  });
}
function swapSection(id, html) {
  const live = document.getElementById(`${SECTION_PREFIX}${id}`);
  if (!live) return;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const next = doc.getElementById(`${SECTION_PREFIX}${id}`) || doc.body.firstElementChild;
  if (next instanceof HTMLElement) {
    live.replaceWith(next);
  } else {
    live.hidden = true;
  }
}
async function renderSections(targets, options = {}) {
  if (targets.length === 0) return;
  const map = options.sections ?? await fetchSections(
    targets.map((t) => t.id),
    options
  );
  for (const target of targets) {
    const html = map[target.id];
    if (typeof html !== "string") continue;
    if (target.selector) {
      swapInner(html, target.selector);
    } else {
      swapSection(target.id, html);
    }
  }
}
async function renderSection(target, options = {}) {
  const html = await fetchSectionHTML(target.id, options);
  if (target.selector) {
    swapInner(html, target.selector);
  } else {
    swapSection(target.id, html);
  }
}

// src/shared/cart-limit-error/cart-limit-error.ts
var ERROR_MESSAGE_SELECTOR = "[data-cart-limit-error-message]";
var ERROR_TEXT_SELECTOR = "[data-cart-limit-error-text]";
var OPEN_ENGRAVING_PANEL_SELECTOR = "c-engraving-panel.is-open";
var SEARCH_DIALOG_SELECTOR = "dialog[data-search-dialog]";
var QUANTITY_LIMIT_PATTERNS = [
  /can'?t add more/i,
  /maximum allowed quantity/i,
  /maximum quantity/i,
  /quantity.*exceeded/i,
  /only add \d+/i
];
function getErrorMessageEl() {
  return document.querySelector(ERROR_MESSAGE_SELECTOR);
}
function getDefaultMessage() {
  return window.cartStrings?.addToCartLimitError ?? "The maximum allowed quantity for this product has been exceeded.";
}
function isCartQuantityLimitError(status, description) {
  if (status !== 422) return false;
  const normalized = description.trim();
  if (normalized.length === 0) return true;
  return QUANTITY_LIMIT_PATTERNS.some((pattern) => pattern.test(normalized));
}
function closeOpenEngravingPanels() {
  document.querySelectorAll(OPEN_ENGRAVING_PANEL_SELECTOR).forEach((panel) => {
    ;
    panel.close();
  });
}
function closeHeaderSearchIfOpen() {
  const openDialogs = document.querySelectorAll(
    `${SEARCH_DIALOG_SELECTOR}[open]`
  );
  if (openDialogs.length === 0) return;
  document.documentElement.classList.remove("is-overflow-hidden");
  openDialogs.forEach((dialog) => {
    dialog.close();
  });
}
function showCartLimitError(message) {
  closeHeaderSearchIfOpen();
  const errorMessage = getErrorMessageEl();
  if (!errorMessage) return;
  const textEl = errorMessage.querySelector(ERROR_TEXT_SELECTOR);
  if (textEl) {
    textEl.textContent = message ?? getDefaultMessage();
  }
  errorMessage.classList.remove("hidden");
  errorMessage.removeAttribute("hidden");
  errorMessage.setAttribute("aria-hidden", "false");
  closeOpenEngravingPanels();
  window.scrollTo(0, 0);
}
function hideCartLimitError() {
  const errorMessage = getErrorMessageEl();
  if (!errorMessage) return;
  const textEl = errorMessage.querySelector(ERROR_TEXT_SELECTOR);
  if (textEl) {
    textEl.textContent = "";
  }
  errorMessage.classList.add("hidden");
  errorMessage.setAttribute("hidden", "");
  errorMessage.setAttribute("aria-hidden", "true");
}

// src/shared/cart-a11y/cart-a11y.ts
var CART_LIVE_STATUS_ID = "cart-live-status";
var FOCUS_ANNOUNCE_DELAY_MS = 300;
function getOrCreateCartLiveRegion() {
  let region = document.getElementById(CART_LIVE_STATUS_ID);
  if (region instanceof HTMLElement) {
    return region;
  }
  region = document.createElement("div");
  region.id = CART_LIVE_STATUS_ID;
  region.className = "sr-only form-status";
  region.setAttribute("role", "status");
  region.setAttribute("aria-live", "polite");
  region.setAttribute("aria-atomic", "true");
  document.body.appendChild(region);
  return region;
}
function announceCartStatus(message, options = {}) {
  const trimmed = message.trim();
  if (!trimmed) return;
  const shouldFocus = options.focus !== false;
  const region = getOrCreateCartLiveRegion();
  if (shouldFocus) {
    region.setAttribute("aria-live", "off");
    region.setAttribute("tabindex", "-1");
    region.textContent = trimmed;
    region.focus({ preventScroll: true });
    window.setTimeout(() => {
      region.setAttribute("aria-live", "polite");
    }, FOCUS_ANNOUNCE_DELAY_MS);
    return;
  }
  region.textContent = "";
  requestAnimationFrame(() => {
    region.textContent = trimmed;
  });
}
function fillCartStatusTemplate(template, values) {
  let result = template;
  if (values.title !== void 0) {
    result = result.replace(/__TITLE__/g, values.title);
  }
  if (values.quantity !== void 0) {
    result = result.replace(/__QUANTITY__/g, String(values.quantity));
  }
  return result;
}

// src/shared/insights/algolia-insights.ts
var ENDPOINT = "https://insights.algolia.io/1/events";
var AGENT = "diptyque-theme";
var TOKEN_KEY = "algolia_user_token";
var PIXEL_CONFIG_KEY = "algolia_config";
var PIXEL_CLICK_KEY = "algolia_analytics_clicked_objects";
var PIXEL_CLICK_MAX = 50;
var MAX_OBJECT_IDS = 20;
var THEME_EMITS_ADD_TO_CART = true;
var OBJECT_ID_SELECTOR = "[data-algolia-object-id]";
var CLICK_HOOKS = "[data-algolia-click], [data-algolia-convert]";
function readConfig() {
  const data = document.body?.dataset;
  if (!data?.algoliaAppId || !data.algoliaApiKey || !data.algoliaIndex) {
    return null;
  }
  return {
    appId: data.algoliaAppId,
    apiKey: data.algoliaApiKey,
    index: data.algoliaIndex
  };
}
function readStorage(key) {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}
function writeStorage(key, value) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
  }
}
function randomUserToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return `anonymous-${Array.from(
    bytes,
    (byte) => byte.toString(36).padStart(2, "0")
  ).join("")}`;
}
var cachedUserToken = null;
function userToken() {
  if (cachedUserToken) return cachedUserToken;
  const stored = readStorage(TOKEN_KEY);
  cachedUserToken = stored || randomUserToken();
  if (!stored) writeStorage(TOKEN_KEY, cachedUserToken);
  return cachedUserToken;
}
function authenticatedUserToken() {
  return document.body?.dataset.algoliaAuthenticatedUserToken || void 0;
}
function normalizeObjectIDs(values) {
  const ids = /* @__PURE__ */ new Set();
  for (const value of values) {
    const id = String(value ?? "").trim();
    if (id) ids.add(id);
  }
  return Array.from(ids).slice(0, MAX_OBJECT_IDS);
}
function send(config, event) {
  const params = new URLSearchParams({
    "X-Algolia-Application-Id": config.appId,
    "X-Algolia-API-Key": config.apiKey,
    "X-Algolia-Agent": AGENT
  });
  const url = `${ENDPOINT}?${params}`;
  const body = JSON.stringify({ events: [event] });
  if (navigator.sendBeacon?.(url, body)) return;
  void fetch(url, { method: "POST", body, keepalive: true }).catch(() => {
  });
}
function track(config, objectIDs, event) {
  if (objectIDs.length === 0) return;
  send(config, {
    ...event,
    index: config.index,
    objectIDs,
    userToken: userToken(),
    authenticatedUserToken: authenticatedUserToken()
  });
}
function writePixelConfig(config) {
  writeStorage(
    PIXEL_CONFIG_KEY,
    JSON.stringify({
      app_id: config.appId,
      search_api_key: config.apiKey,
      index_name: config.index,
      user_token: userToken()
    })
  );
}
function recordClickForPixel(config, objectIDs, productId) {
  const stored = readStorage(PIXEL_CLICK_KEY);
  let records = [];
  try {
    const parsed = stored ? JSON.parse(stored) : [];
    if (Array.isArray(parsed)) records = parsed;
  } catch {
    records = [];
  }
  records.push({ index: config.index, objectIDs, productId });
  writeStorage(PIXEL_CLICK_KEY, JSON.stringify(records.slice(-PIXEL_CLICK_MAX)));
}
function readObjectId(element) {
  const above = element?.closest(OBJECT_ID_SELECTOR);
  if (above?.dataset.algoliaObjectId) return above.dataset.algoliaObjectId;
  const inside = element?.querySelector(OBJECT_ID_SELECTOR);
  return inside?.dataset.algoliaObjectId ?? "";
}
function readProductId(element) {
  const above = element?.closest("[data-algolia-product-id]");
  if (above?.dataset.algoliaProductId) return above.dataset.algoliaProductId;
  const inside = element?.querySelector(
    "[data-algolia-product-id]"
  );
  return inside?.dataset.algoliaProductId ?? "";
}
function onDocumentClick(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const hook = target.closest(CLICK_HOOKS);
  if (!hook) return;
  const config = readConfig();
  if (!config) return;
  const objectIDs = normalizeObjectIDs([readObjectId(hook)]);
  if (objectIDs.length === 0) return;
  writePixelConfig(config);
  recordClickForPixel(config, objectIDs, readProductId(hook));
  if (hook.matches("[data-algolia-convert]")) return;
  track(config, objectIDs, { eventType: "click", eventName: "Product Clicked" });
}
function onCartAddSuccess(event) {
  if (!THEME_EMITS_ADD_TO_CART) return;
  const target = event.target;
  if (!(target instanceof Element)) return;
  const config = readConfig();
  if (!config) return;
  const objectIDs = normalizeObjectIDs([readObjectId(target)]);
  if (objectIDs.length === 0) return;
  writePixelConfig(config);
  track(config, objectIDs, {
    eventType: "conversion",
    eventSubtype: "addToCart",
    eventName: "Add To Cart"
  });
}
function initAlgoliaInsights() {
  if (typeof window === "undefined" || window.algoliaInsightsReady) return;
  window.algoliaInsightsReady = true;
  document.addEventListener("click", onDocumentClick, true);
  document.addEventListener("cart:add-success", onCartAddSuccess);
}

// src/snippets/product-form/product-form.ts
var TECHNICAL_ERROR_PATTERN = /parameter/i;
var STOCK_LIMIT_PATTERN = /due to availability/i;
var DEFAULT_CART_ERROR = "There was an error while updating your cart. Please try again.";
var CartRequestError = class extends Error {
  constructor(status, technical, lineKeys, message) {
    super(message);
    this.status = status;
    this.technical = technical;
    this.lineKeys = lineKeys;
  }
};
function namedLineKeys(formData) {
  return [...formData.keys()].flatMap(
    (name) => /^updates\[(.+)\]$/.exec(name)?.[1] ?? []
  );
}
var CProductForm = class extends HTMLElement {
  constructor() {
    super(...arguments);
    this.SELECTORS = {
      form: "form",
      submitButton: 'button[type="submit"]',
      updatesInput: 'input[name^="updates["]',
      // Champ `updates[…]` que les boutons `+` / `−` ne touchent pas : la ligne
      // cadeau, dont la quantité est fixée à 1 par le Liquid.
      qtyFixedInput: "[data-qty-fixed]",
      qtyButton: "[data-qty-btn]",
      notifyMeButton: "[data-notify-me-button]",
      notifyWidget: '.gw-float-widget[type="button"]',
      loaderId: "full-page-loader",
      cartRecommendRoot: "[data-cart-recommend-root]",
      // Section `cart-samples` : ancre stable du wrapper + les deux blocs internes.
      samplesSection: "[data-samples-section]",
      samplesRoot: "[data-cart-samples]",
      packagingRoot: "[data-cart-packaging]",
      // Targets re-render via l'Ajax Section Rendering API.
      giftLine: "[data-gift-line]",
      headerCartLink: ".header__action-link--cart",
      popupAtcContainer: "[data-popup-atc-container]",
      cartItems: "[data-cart-items]",
      // Cartes du panier, ancrées à leur clé de ligne — voir `restoreCartOrder`.
      cartLine: "[data-cart-items] [data-line-key]",
      engravingPanelCart: ".engraving-panel-container-cart",
      cartSummary: "[data-cart-summary]",
      // Wrapper `#shopify-section-…` de la section cart-summary : présent même
      // quand le panier est vide (le contenu interne, lui, est conditionné par
      // `cart.item_count`), donc plus fiable que `[data-cart-summary]` pour
      // retrouver l'id de section.
      cartSummarySection: ".section-cart-summary"
    };
    // Initialisés ici et non dans le constructeur : `readDom()` les remplit au
    // premier usage, quand les enfants et les attributs existent enfin.
    this.form = null;
    this.countCartItems = null;
    this.formId = null;
    this.btnSubmit = null;
    this.btnSubmitAlreadyInfos = null;
    this.type = null;
    this.isCart = null;
    this.itemKey = null;
    this.action = "add";
    this.errorMessages = {};
    this.quantitySnapshot = null;
    this.samplesSectionId = null;
    this.sectionsRenderSamples = [];
    this.sectionsRender = [];
    this.hasConnected = false;
    this.submitForm = async () => {
      if (!this.form) return;
      this.clearCartError();
      const hiddenElements = Array.from(
        this.form.querySelectorAll("input, select, textarea")
      ).filter(
        (el) => el.offsetParent === null && el.type !== "hidden"
      );
      hiddenElements.forEach((el) => {
        el.disabled = true;
      });
      const formData = new FormData(this.form);
      hiddenElements.forEach((el) => {
        el.disabled = false;
      });
      const config = this.fetchConfig("javascript");
      const targets = this.withGiftLineTarget(
        this.isCart ? this.getCartPageTargets() : this.sectionsRender
      );
      const sectionsParam = targets.map((target) => target.id).join(",");
      formData.set("sections", sectionsParam);
      if (this.action === "change" && this.itemKey) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          if (key.includes("properties")) {
            const propKey = key.match(/\[(.*?)\]/g)?.pop()?.replace(/[[\]]/g, "");
            if (propKey) properties[propKey] = value;
          }
        }
        config.body = JSON.stringify({
          id: this.itemKey,
          properties,
          sections: sectionsParam
        });
      } else {
        config.body = formData;
        delete config.headers["Content-Type"];
      }
      config.headers["X-Requested-With"] = "XMLHttpRequest";
      try {
        const endpoints = {
          add: "cart/add.js",
          change: "cart/change.js",
          update: "cart/update.js",
          remove: "cart/update.js",
          // Monter d'un cran est un simple ajout, que la Cart Transform fusionne
          // avec les lignes en place. Descendre n'a pas d'endpoint — voir
          // `setVirtualSetQuantity` juste en dessous.
          add_virtual_set: "cart/add.js"
        };
        const endpoint = endpoints[this.action] ?? endpoints["add"];
        if (this.action === "set_virtual_set") {
          await this.setVirtualSetQuantity(formData, sectionsParam, targets);
          return;
        }
        const response = await fetch(
          (window.Shopify?.routes?.root || "/") + endpoint,
          config
        );
        if (!response.ok) {
          throw await this.readCartError(
            response,
            this.action === "change" && this.itemKey ? [this.itemKey] : namedLineKeys(formData)
          );
        }
        const data = await response.json();
        hideCartLimitError();
        this.quantitySnapshot = null;
        const statusAnnouncement = this.buildCartMutationAnnouncement();
        await renderSections(targets, { sections: data.sections });
        if (statusAnnouncement) {
          announceCartStatus(statusAnnouncement);
        }
        this.dispatchEvent(
          new CustomEvent("cart:add-success", {
            bubbles: true,
            detail: { data, action: this.action }
          })
        );
        if (this.isCart) {
          this.renderSampleSection();
          await this.rerenderCartRecommendSections();
        }
      } catch (error) {
        const removal = this.isLineRemoval();
        const removalAnnouncement = removal ? this.buildCartMutationAnnouncement() : null;
        this.restoreQuantitySnapshot();
        console.error(error.message);
        if (this.isCart) await this.resyncCartPage(targets);
        if (!(error instanceof CartRequestError)) return;
        if (removal && this.isCart && !this.isLineShown(this.itemKey)) {
          if (removalAnnouncement) announceCartStatus(removalAnnouncement);
          return;
        }
        this.showCartError(error);
      }
    };
  }
  /**
   * Resolves the element's children and attributes, on demand.
   *
   * This used to run in the constructor, which is the one place it cannot: a
   * custom element constructor executes at `createElement` / at the opening tag,
   * **before any child or attribute exists**. The component only ever worked
   * because its definition happened to arrive *after* the subtree was complete,
   * so the upgrade replayed the constructor on a finished element — an accident
   * of load order, not a guarantee. The day an entry point imported this module
   * eagerly, the definition landed first and every field below came out null:
   * no submit listener, and quick-buy fell back to a native POST that navigated
   * to /cart instead of adding over AJAX.
   *
   * `connectedCallback` is no better on its own — for server-rendered HTML it
   * fires on the opening tag, also before the children. So the reads happen
   * here, called from the delegated handlers at interaction time, when the
   * subtree is complete whichever way it was built.
   *
   * Idempotent and cheap; re-resolving picks up a subtree the theme editor or a
   * section re-render swapped underneath us.
   */
  readDom() {
    this.form = this.querySelector(this.SELECTORS.form);
    this.countCartItems = this.dataset.countCartItems || null;
    this.formId = this.form?.id || null;
    this.btnSubmit = this.querySelector(this.SELECTORS.submitButton);
    this.btnSubmitAlreadyInfos = document.querySelector(
      `button[form="${this.formId}"]`
    );
    this.type = this.dataset.type || null;
    this.isCart = this.dataset.isCart || (document.body.classList.contains("body--cart") ? "true" : null);
    this.itemKey = this.dataset.itemKey || null;
    this.action = this.dataset.action || "add";
    this.errorMessages = {
      error_product_gift_cart: this.dataset.errorProductGiftCart || ""
    };
    this.samplesSectionId = this.getSamplesSectionId();
    this.sectionsRenderSamples = this.samplesSectionId ? [
      {
        id: this.samplesSectionId,
        selector: [
          this.SELECTORS.samplesRoot,
          this.SELECTORS.packagingRoot
        ]
      }
    ] : [];
    this.sectionsRender = [
      { id: "header", selector: [this.SELECTORS.headerCartLink] },
      { id: "popup-add-to-cart", selector: [this.SELECTORS.popupAtcContainer] }
    ];
  }
  getSamplesSectionId() {
    const sectionChild = document.querySelector(this.SELECTORS.samplesSection);
    return closestSectionId(sectionChild);
  }
  getCartSummarySectionId() {
    return closestSectionId(
      document.querySelector(this.SELECTORS.cartSummarySection)
    ) ?? closestSectionId(document.querySelector(this.SELECTORS.cartSummary));
  }
  // Page panier éclatée en plusieurs sections empilées. Les ids sont résolus au
  // moment du submit (et non dans le constructeur) : le wrapper cart-summary
  // peut être absent ou remplacé entre deux mutations du panier, et l'id réel
  // dépend de templates/cart.json (l'éditeur de thème peut le suffixer).
  getCartPageTargets() {
    const cartSummarySectionId = this.getCartSummarySectionId();
    return [
      { id: "header", selector: [this.SELECTORS.headerCartLink] },
      { id: "popup-add-to-cart", selector: [this.SELECTORS.popupAtcContainer] },
      {
        id: "main-cart",
        selector: [this.SELECTORS.cartItems, this.SELECTORS.engravingPanelCart]
      },
      // Outer swap (pas de `selector`) : le contenu de cart-summary est gated
      // par `cart.item_count`, un inner swap ne saurait pas passer de vide à
      // non-vide (et inversement).
      ...cartSummarySectionId ? [{ id: cartSummarySectionId }] : []
    ];
  }
  /**
   * Ajoute la ligne cadeau de CE formulaire aux cibles de re-rendu.
   *
   * Un produit ouvre droit à un cadeau, et le Liquid tranche au rendu — mais
   * après un ajout, seuls le header et le popup reviennent : le formulaire
   * resté à l'écran garderait ses inputs cadeau et en servirait un second au
   * clic suivant. On redemande donc au serveur la section qui le porte, et on
   * n'en réinjecte que le conteneur `[data-gift-line]` : re-rendre la section
   * entière écraserait la variante choisie et la quantité saisie.
   *
   * La section est résolue depuis le DOM plutôt que codée en dur, parce que le
   * formulaire vit dans une douzaine de sections différentes (PDP, cross-sell,
   * barre collante, cartes produit…). Une carte rendue côté client — l'îlot
   * Algolia — n'a pas de section porteuse : `closestSectionId` répond `null` et
   * rien n'est ajouté, l'îlot ayant sa propre lecture du panier.
   */
  withGiftLineTarget(targets) {
    if (!this.form?.querySelector(this.SELECTORS.giftLine)) return targets;
    const id = closestSectionId(this);
    if (!id) return targets;
    const existing = targets.find((target) => target.id === id);
    if (!existing) {
      return [...targets, { id, selector: [this.SELECTORS.giftLine] }];
    }
    if (!existing.selector) return targets;
    const selectors = Array.isArray(existing.selector) ? existing.selector : [existing.selector];
    if (selectors.includes(this.SELECTORS.giftLine)) return targets;
    return targets.map(
      (target) => target === existing ? { ...target, selector: [...selectors, this.SELECTORS.giftLine] } : target
    );
  }
  /**
   * Every listener is delegated on the host, not on `this.form`.
   *
   * `submit`, `change`, `click` and `focusin` all bubble, so binding on the
   * element itself works whether the children were parsed from HTML (they do
   * not exist yet at this point) or built by Preact. Binding on `this.form`
   * required the form to already exist, which is precisely what could not be
   * relied on — see `readDom()`.
   */
  connectedCallback() {
    if (this.hasConnected) return;
    this.hasConnected = true;
    setTimeout(() => {
      this.readDom();
      this.initNotifyMe();
      this.checkNotifyMeHash();
    }, 0);
    this.addEventListener("submit", async (e) => {
      this.readDom();
      if (!this.form) return;
      e.preventDefault();
      if (this.btnSubmit) this.btnSubmit.setAttribute("disabled", "true");
      if (this.btnSubmitAlreadyInfos)
        this.btnSubmitAlreadyInfos.setAttribute("disabled", "true");
      const loader = document.getElementById(this.SELECTORS.loaderId);
      if (loader) loader.setAttribute("aria-hidden", "false");
      await this.submitForm();
      if (loader) loader.setAttribute("aria-hidden", "true");
      if (this.btnSubmit) this.btnSubmit.removeAttribute("disabled");
      if (this.btnSubmitAlreadyInfos)
        this.btnSubmitAlreadyInfos.removeAttribute("disabled");
    });
    this.addEventListener("focusin", (e) => {
      this.readDom();
      if (this.action !== "update") return;
      const target = e.target;
      if (target.name?.startsWith("updates[")) this.captureQuantitySnapshot();
    });
    this.addEventListener("change", (e) => {
      const target = e.target;
      if (target.name?.includes("updates[")) {
        this.readDom();
        this.form?.requestSubmit();
      }
    });
    this.addEventListener("click", (e) => {
      const button = e.target.closest(
        this.SELECTORS.qtyButton
      );
      if (!button) return;
      this.readDom();
      if (!this.form) return;
      e.preventDefault();
      const step = parseInt(button.getAttribute("data-qty-step") || "0", 10);
      const inputs = Array.from(
        this.form.querySelectorAll(
          this.SELECTORS.updatesInput
        )
      );
      const stepped = inputs.filter(
        (el) => !el.matches(this.SELECTORS.qtyFixedInput)
      );
      const fixed = inputs.filter(
        (el) => el.matches(this.SELECTORS.qtyFixedInput)
      );
      let blockedByLimit = false;
      this.captureQuantitySnapshot();
      stepped.forEach((el) => {
        const currentVal = parseInt(el.value || "0", 10);
        const maxAttr = el.getAttribute("max");
        const maxQuantity = maxAttr ? parseInt(maxAttr, 10) : Number.NaN;
        const nextVal = currentVal + step;
        if (step > 0 && Number.isFinite(maxQuantity) && nextVal > maxQuantity) {
          blockedByLimit = true;
          return;
        }
        el.value = Math.max(0, nextVal).toString();
      });
      if (blockedByLimit) {
        this.quantitySnapshot = null;
        showCartLimitError();
        return;
      }
      const parentRemains = stepped.some(
        (el) => parseInt(el.value || "0", 10) > 0
      );
      fixed.forEach((el) => {
        el.value = parentRemains ? "1" : "0";
      });
      this.form.requestSubmit();
    });
  }
  /**
   * If the product is unavailable, the form renders an `<a data-notify-me-button>` link
   * instead of a submit button. This method polls for the third-party notify-me widget
   * and proxies clicks from our button to that widget.
   */
  initNotifyMe() {
    const notifyMeBtn = this.querySelector(this.SELECTORS.notifyMeButton);
    if (!notifyMeBtn) return;
    const href = notifyMeBtn.getAttribute("href") || "";
    if (href && !href.includes("#notify-me")) {
      notifyMeBtn.setAttribute("href", `${href}#notify-me`);
    }
    const bindNotifyWidget = (retries = 20, interval = 300) => {
      const widget = document.querySelector(
        this.SELECTORS.notifyWidget
      );
      if (widget) {
        widget.style.display = "none";
        notifyMeBtn.addEventListener("click", (event) => {
          event.preventDefault();
          widget.click();
        });
      } else if (retries > 0) {
        setTimeout(() => bindNotifyWidget(retries - 1, interval), interval);
      }
    };
    bindNotifyWidget();
  }
  /**
   * When landing on a product page with `#notify-me` in the URL (e.g. from a
   * collection card), auto-open the third-party notify-me widget.
   */
  checkNotifyMeHash() {
    if (window.location.hash !== "#notify-me") return;
    const autoOpenNotifyWidget = (retries = 20, interval = 300) => {
      const widget = document.querySelector(
        this.SELECTORS.notifyWidget
      );
      if (widget) {
        widget.click();
        history.replaceState(
          null,
          "",
          window.location.pathname + window.location.search
        );
      } else if (retries > 0) {
        setTimeout(() => autoOpenNotifyWidget(retries - 1, interval), interval);
      }
    };
    autoOpenNotifyWidget();
  }
  // Re-render la section `cart-samples` via l'Ajax Section Rendering API
  // (`?section_id=`) puis swap les blocs samples + packaging. Reload complet en
  // cas d'erreur.
  async renderSampleSection() {
    try {
      for (const section of this.sectionsRenderSamples) {
        await renderSection(section);
      }
    } catch (error) {
      console.error("[CartSamples] section render error:", error);
      window.location.reload();
    }
  }
  /**
   * Puts the cart page back in line with the cart after a failed mutation.
   *
   * The page can show a cart that has moved on — a line removed from another
   * tab, a page Safari brought back from its back/forward cache. Its inputs
   * then name line keys the cart no longer has, and `cart/update.js` refuses
   * the whole payload with a 422. Left on screen, those inputs were sent again
   * by every retry, which failed the same way. Same targets as a successful
   * mutation, fetched instead of read from the response.
   *
   * The recommendations are left alone: an Algolia round-trip and a carousel
   * back at its start, for no gain. A failed fetch keeps the page as it is —
   * no reload, unlike `renderSampleSection`: offline, that would trade the
   * cart for the browser's error page.
   */
  async resyncCartPage(targets) {
    try {
      await renderSections(targets);
      for (const section of this.sectionsRenderSamples) {
        await renderSection(section);
      }
    } catch (error) {
      console.error("[Cart] resync error:", error);
    }
  }
  /** Whether this submit takes its line out of the cart: the ✕, or `−` to 0. */
  isLineRemoval() {
    if (this.action === "remove") return true;
    return this.action === "update" && this.readAnnouncedQuantity() === 0;
  }
  /** Whether the cart page shows the line of this key. True without a key. */
  isLineShown(key) {
    if (!key) return true;
    return Boolean(
      document.querySelector(
        `${this.SELECTORS.cartItems} [data-line-key="${CSS.escape(key)}"]`
      )
    );
  }
  /**
   * Whether the refused request named a line the cart no longer has: the page
   * was behind the cart, and Shopify refused the whole payload for it — in the
   * shop's language, so the key tells, not the wording. Every line but the
   * samples shows its key, gift lines included, and no form of this element
   * names a sample. Read once the page is back in line with the cart: a failed
   * resync leaves every line shown.
   */
  isStale(error) {
    if (!this.isCart) return false;
    return error.lineKeys.some((key) => !this.isLineShown(key));
  }
  /**
   * Pose le nombre de coffrets voulu : on vide la ligne, puis on la repose.
   *
   * Aucun endpoint de l'Ajax Cart API ne sait retrancher sur une ligne
   * fusionnée. Elle rapporte toujours `quantity: 1`, donc tout ce qu'on lui
   * écrit est consommé comme facteur : depuis n coffrets on n'atteint que n×k
   * ou n+m, et `n−1` est hors du domaine. Vérifié levier par levier en
   * boutique, y compris les clés de `item_components`, que Liquid rend mais que
   * `updates` refuse en 422.
   *
   * Le formulaire porte les deux moitiés côte à côte — le `updates[…] = 0` qui
   * vide la ligne, et les `items[…]` qui la reposent à la cible. On les sépare
   * ici parce qu'elles ne partent pas au même endpoint.
   *
   * L'ordre n'est pas négociable : reposer avant de vider ferait fusionner le
   * nouvel envoi avec l'ancien, et doublerait le coffret. La contrepartie est
   * assumée — si le second appel échoue, le coffret est déjà sorti du panier —
   * et n'a pas d'alternative tant que la Cart Transform fixe la quantité à 1.
   *
   * Seule la réponse du réajout porte les sections : c'est l'état final. Rendre
   * celles de la suppression ferait clignoter un panier sans le coffret.
   */
  async setVirtualSetQuantity(formData, sectionsParam, targets) {
    const removals = new FormData();
    const additions = new FormData();
    for (const [key, value] of formData.entries()) {
      if (key.startsWith("updates[")) {
        removals.append(key, value);
      } else if (key.startsWith("items[")) {
        additions.append(key, value);
      }
    }
    if (![...additions.keys()].length) {
      throw new Error("[VirtualSet] aucun items[\u2026] \xE0 reposer");
    }
    additions.set("sections", sectionsParam);
    const order = this.readCartOrder();
    const root = window.Shopify?.routes?.root || "/";
    const headers = { "X-Requested-With": "XMLHttpRequest" };
    if ([...removals.keys()].length) {
      const removed = await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers,
        body: removals
      });
      if (!removed.ok) {
        throw await this.readCartError(removed, namedLineKeys(removals));
      }
    }
    const added = await fetch(`${root}cart/add.js`, {
      method: "POST",
      headers,
      body: additions
    });
    if (!added.ok) throw await this.readCartError(added);
    const data = await added.json();
    hideCartLimitError();
    this.quantitySnapshot = null;
    const statusAnnouncement = this.buildCartMutationAnnouncement();
    await renderSections(targets, { sections: data.sections });
    this.restoreCartOrder(order);
    if (statusAnnouncement) {
      announceCartStatus(statusAnnouncement);
    }
    this.dispatchEvent(
      new CustomEvent("cart:add-success", {
        bubbles: true,
        detail: { data, action: this.action }
      })
    );
    if (this.isCart) {
      this.renderSampleSection();
      await this.rerenderCartRecommendSections();
    }
  }
  /** Les clés de ligne des cartes du panier, dans l'ordre où elles s'affichent. */
  readCartOrder() {
    return [
      ...document.querySelectorAll(this.SELECTORS.cartLine)
    ].flatMap((card) => card.dataset.lineKey ? [card.dataset.lineKey] : []);
  }
  /**
   * Remet les cartes du panier dans l'ordre relevé avant l'opération.
   *
   * Second filet, et non doublon de l'épinglage que fait `main-cart` : celui-ci
   * fixe la position de la FAMILLE — les coffrets en tête, l'emballage cadeau
   * en queue — mais laisse les coffrets entre eux dans l'ordre de `cart.items`.
   * Or descendre la quantité de l'un impose de vider sa ligne pour la reposer,
   * ce qui l'envoie en fin de tableau : avec deux coffrets au panier, agir sur
   * le premier le ferait passer derrière le second.
   *
   * Rejouer l'ordre relevé au clic répare ce cas-là, que l'épinglage ne couvre
   * pas. Réordonner et non figer : personne n'est assigné à un rang, et les
   * cartes absentes du relevé — une ligne apparue entre-temps — restent après,
   * dans leur ordre courant.
   *
   * Ne survit pas à un rechargement, où l'ordre de `cart.items` reprend la
   * main. Le stabiliser demanderait de mémoriser l'ordre côté serveur.
   *
   * La clé de ligne survit au vidage-réajout : vérifié en boutique, elle est
   * identique avant et après, ce qui est ce qui rend l'appariement possible.
   */
  restoreCartOrder(order) {
    if (order.length < 2) return;
    const cards = [
      ...document.querySelectorAll(this.SELECTORS.cartLine)
    ];
    const parent = cards[0]?.parentElement;
    if (!parent) return;
    const byKey = new Map(
      cards.filter((card) => card.parentElement === parent && card.dataset.lineKey).map((card) => [card.dataset.lineKey, card])
    );
    for (const key of order) {
      const card = byKey.get(key);
      if (card) {
        parent.appendChild(card);
        byKey.delete(key);
      }
    }
    for (const card of byKey.values()) parent.appendChild(card);
  }
  /**
   * Reads a failed Cart API answer. Shown by `submitForm`, once the cart page
   * is back in line with the cart: by then the error may have nothing left to
   * report. `lineKeys`: the cart lines the request named.
   */
  async readCartError(response, lineKeys = []) {
    const errorData = await response.json().catch(() => ({}));
    const text = errorData.description || errorData.message;
    return new CartRequestError(
      response.status,
      !text || TECHNICAL_ERROR_PATTERN.test(text),
      lineKeys,
      text || `Erreur ${response.status}`
    );
  }
  /** The banner of a failed Cart API answer — never Shopify's technical wording. */
  showCartError(error) {
    const rawMessage = error.message;
    if (isCartQuantityLimitError(error.status, rawMessage) || STOCK_LIMIT_PATTERN.test(rawMessage)) {
      showCartLimitError();
      return;
    }
    const fallback = error.technical || this.isStale(error) ? this.dataset.errorMessage || DEFAULT_CART_ERROR : rawMessage;
    showCartLimitError(this.errorMessages[rawMessage] || fallback);
  }
  captureQuantitySnapshot() {
    if (!this.form || this.action !== "update") return;
    this.quantitySnapshot = /* @__PURE__ */ new Map();
    this.form.querySelectorAll(this.SELECTORS.updatesInput).forEach((input) => {
      this.quantitySnapshot.set(input.name, input.value);
    });
  }
  restoreQuantitySnapshot() {
    if (!this.form || !this.quantitySnapshot) return;
    this.form.querySelectorAll(this.SELECTORS.updatesInput).forEach((input) => {
      const saved = this.quantitySnapshot.get(input.name);
      if (saved !== void 0) input.value = saved;
    });
    this.quantitySnapshot = null;
  }
  clearCartError() {
    hideCartLimitError();
  }
  /**
   * Builds a screen-reader message for a successful cart mutation, or null when
   * another surface (ATC popup) already announces the result.
   */
  buildCartMutationAnnouncement() {
    const title = this.dataset.productTitle?.trim() || "Item";
    if (this.action === "remove") {
      const template = this.dataset.itemRemovedTemplate || "__TITLE__ removed from bag";
      return fillCartStatusTemplate(template, { title });
    }
    if (this.action === "update" || this.action === "set_virtual_set" || this.action === "add_virtual_set" && this.isCart) {
      const quantity = this.readAnnouncedQuantity();
      if (quantity === 0) {
        const removedTemplate = this.dataset.itemRemovedTemplate || "__TITLE__ removed from bag";
        return fillCartStatusTemplate(removedTemplate, { title });
      }
      if (quantity !== null) {
        const template = this.dataset.quantityUpdatedTemplate || "__TITLE__ quantity updated to __QUANTITY__";
        return fillCartStatusTemplate(template, { title, quantity });
      }
    }
    if ((this.action === "add" || this.action === "add_virtual_set") && this.isCart) {
      const template = this.dataset.itemAddedTemplate || "__TITLE__ was added to bag";
      return fillCartStatusTemplate(template, { title });
    }
    return null;
  }
  /**
   * Resolves the quantity to announce after a cart stepper mutation.
   */
  readAnnouncedQuantity() {
    if (!this.form) return null;
    if (this.action === "set_virtual_set") {
      const quantityInput = this.form.querySelector(
        'input[name^="items["][name$="[quantity]"]'
      );
      if (!quantityInput) return null;
      const value2 = parseInt(quantityInput.value, 10);
      return Number.isFinite(value2) ? value2 : null;
    }
    if (this.action === "add_virtual_set") {
      const lineRoot = this.closest("[data-line-key]");
      const displayInput = lineRoot?.querySelector(
        `${this.SELECTORS.updatesInput}:not(${this.SELECTORS.qtyFixedInput})`
      );
      if (!displayInput) return null;
      const current = parseInt(displayInput.value, 10);
      if (!Number.isFinite(current)) return null;
      return current + 1;
    }
    const updatesInput = this.form.querySelector(
      `${this.SELECTORS.updatesInput}:not(${this.SELECTORS.qtyFixedInput})`
    );
    if (!updatesInput) return null;
    const value = parseInt(updatesInput.value, 10);
    return Number.isFinite(value) ? value : null;
  }
  fetchConfig(type = "json") {
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: `application/${type}`
      }
    };
  }
  // Outer swap des sections de recommandations : on collecte les ids des
  // wrappers `#shopify-section-…` autour de chaque `[data-cart-recommend-root]`
  // puis on remplace chaque wrapper par sa version fraîche.
  async rerenderCartRecommendSections() {
    const roots = document.querySelectorAll(this.SELECTORS.cartRecommendRoot);
    const sectionIds = collectSectionIds(roots);
    if (sectionIds.length === 0) return;
    await renderSections(sectionIds.map((id) => ({ id })));
  }
  disconnectedCallback() {
  }
};
if (!customElements.get("c-product-form")) {
  customElements.define("c-product-form", CProductForm);
  window.addEventListener("pageshow", (event) => {
    if (event.persisted && document.body.classList.contains("body--cart")) {
      window.location.reload();
    }
  });
  const [navigation] = performance.getEntriesByType(
    "navigation"
  );
  if (navigation?.type === "back_forward" && !(navigation.transferSize > 0) && document.body.classList.contains("body--cart")) {
    window.location.reload();
  }
}
initAlgoliaInsights();

// src/sections/algolia-header-search/constants.ts
var MAX_PREDICTIVE_QUERIES = 4;
var RECENT_SEARCHES_KEY = "diptyque:algolia-header-search:recent-searches";
var MAX_RECENT_SEARCHES = 3;
var MAX_REMEMBERED_REDIRECTS = 20;
var PRODUCTS_RULE_CONTEXTS = ["explore"];
var PRODUCT_ATTRIBUTES_TO_RETRIEVE = [
  "objectID",
  "id",
  "handle",
  "url",
  "title",
  "image",
  "meta",
  "subtitle",
  "price",
  "available",
  "inventory_available",
  "variant_id",
  "template_suffix"
];
var SIDEBAR_ATTRIBUTES_TO_RETRIEVE = [
  "objectID",
  "handle",
  "title",
  "url",
  "type",
  "object_type",
  "products_count",
  "product_type",
  // Notation pointée : Algolia ne renvoie que ce sous-arbre de `meta`, pas le
  // `meta` complet d'une fiche produit.
  "meta.custom.front_display_title"
];
var QUERY_ATTRIBUTES_TO_RETRIEVE = [
  "objectID",
  "query",
  "meta",
  "title",
  "url"
];

// src/sections/algolia-header-search/components/App.tsx
import { render } from "preact";
import {
  searchUrlFor as searchUrlFor2
} from "@theme/algolia-shared";

// src/sections/algolia-header-search/components/Panel.tsx
import {
  escapeHtml,
  itemUrl as itemUrl2,
  renderRulePushIconic,
  renderRulePushIconics,
  searchUrlFor,
  shopifyRoot
} from "@theme/algolia-shared";

// src/sections/algolia-header-search/components/ProductCard.tsx
import {
  AddToCartInputs,
  giftFromHit,
  ICON_BULLET,
  isGiftEligible,
  isSetToComposeProduct,
  itemUrl,
  preorderButtonLabel,
  productTagLabels,
  renderProductPrice,
  virtualSetChildVariantIds
} from "@theme/algolia-shared";
import { jsx, jsxs } from "preact/jsx-runtime";
function ProductForm({
  contenanceLabel,
  hit,
  isCartPage,
  resolvedGift,
  variantId
}) {
  const formId = `algolia-card-${variantId}`;
  const gift = isGiftEligible(hit) ? giftFromHit(hit) : null;
  const needsGiftResolution = Boolean(gift && "productId" in gift && hit.handle);
  const giftPending = Boolean(needsGiftResolution && resolvedGift === void 0);
  const sectionsToRender = isCartPage ? "header,popup-add-to-cart,cart-samples,cart-summary,main-cart" : "header,popup-add-to-cart";
  const strings = window.variantStrings;
  const url = itemUrl(hit);
  const available = !Object.is(hit.inventory_available, false);
  const notifyUrl = url.includes("#notify-me") ? url : `${url}#notify-me`;
  const preorderLabel = preorderButtonLabel(hit);
  const linksToComposePage = isSetToComposeProduct(hit) && virtualSetChildVariantIds(hit, variantId).length === 0;
  return /* @__PURE__ */ jsx("div", { class: "product-form", children: /* @__PURE__ */ jsx(
    "c-product-form",
    {
      class: "product-form__form",
      "data-action": "add",
      "data-product-id": hit.id ?? "",
      "data-variant-id": variantId,
      ...isCartPage ? { "data-is-cart": "true" } : {},
      children: /* @__PURE__ */ jsxs(
        "form",
        {
          id: formId,
          class: "form",
          action: "/cart/add",
          method: "post",
          novalidate: true,
          "data-type": "add-to-cart-form",
          ...needsGiftResolution ? {
            "data-gift-parent-handle": hit.handle,
            "data-gift-parent-id": String(variantId)
          } : {},
          children: [
            /* @__PURE__ */ jsx("input", { type: "hidden", value: sectionsToRender, name: "sections" }),
            /* @__PURE__ */ jsx(
              AddToCartInputs,
              {
                contenanceLabel,
                hit,
                resolvedGift,
                variantId
              }
            ),
            !available ? /* @__PURE__ */ jsx(
              "a",
              {
                class: "button button-primary button--header-search",
                href: notifyUrl,
                "data-algolia-click": true,
                children: /* @__PURE__ */ jsx("span", { children: strings?.notify_me ?? "Notify me" })
              }
            ) : linksToComposePage ? /* @__PURE__ */ jsx(
              "a",
              {
                class: "button button-primary button--header-search",
                href: url,
                "data-algolia-click": true,
                children: /* @__PURE__ */ jsx("span", { children: strings?.set_to_compose ?? "Create this set" })
              }
            ) : /* @__PURE__ */ jsx(
              "button",
              {
                type: "submit",
                class: "button product-form__submit button-primary--link",
                "data-product-form-submit": true,
                "data-algolia-convert": true,
                disabled: giftPending,
                "aria-busy": giftPending ? "true" : void 0,
                "data-gift-pending": giftPending ? "" : void 0,
                children: /* @__PURE__ */ jsxs("span", { class: "product-form__submit-text button__label", children: [
                  /* @__PURE__ */ jsx("span", { dangerouslySetInnerHTML: { __html: ICON_BULLET } }),
                  /* @__PURE__ */ jsx("span", { children: preorderLabel || (strings?.addToCart ?? "Add to cart") }),
                  /* @__PURE__ */ jsx("span", { dangerouslySetInnerHTML: { __html: ICON_BULLET } })
                ] })
              }
            ),
            /* @__PURE__ */ jsx(
              "div",
              {
                class: "product-form__error",
                "data-product-form-error": true,
                style: { display: "none" }
              }
            ),
            /* @__PURE__ */ jsx(
              "script",
              {
                type: "application/ld+json",
                style: { display: "none" },
                "data-structured-data-product": true,
                dangerouslySetInnerHTML: {
                  __html: JSON.stringify([{ id: variantId }])
                }
              }
            )
          ]
        }
      )
    }
  ) });
}
function ProductTags({ tags = [] }) {
  const labels = productTagLabels(tags);
  if (!labels.length) return null;
  return /* @__PURE__ */ jsx("div", { class: "product-card__tags-container", children: labels.map((name) => /* @__PURE__ */ jsx("span", { class: "product-card__tag label-s-regular", children: name }, name)) });
}
function ProductCard({
  contenanceLabel,
  hit,
  isCartPage,
  itemProps,
  resolvedGift
}) {
  const url = itemUrl(hit);
  const title = hit.title ?? "";
  const subtitle = hit.meta?.custom?.subtitle ?? hit.subtitle ?? "";
  const variantId = hit.variant_id ?? hit.objectID ?? "";
  const price = renderProductPrice(hit);
  const cardProps = { ...itemProps };
  delete cardProps.onMouseMove;
  return (
    // Cf. la carte de grille : l'`objectID` (id de variante) est posé sur la
    // carte, seul point où il est connu à coup sûr. Lu par
    // src/shared/insights/algolia-insights.ts.
    /* @__PURE__ */ jsxs(
      "div",
      {
        class: "product-card align-center",
        "data-algolia-object-id": String(hit.objectID ?? variantId ?? ""),
        "data-algolia-product-id": String(hit.id ?? ""),
        ...cardProps,
        children: [
          /* @__PURE__ */ jsx(ProductTags, { tags: hit.meta?.custom?.product_tags }),
          /* @__PURE__ */ jsx("div", { class: "product-card__image", children: hit.image ? /* @__PURE__ */ jsx(
            "img",
            {
              src: hit.image,
              alt: "",
              class: "product-card__image-item",
              loading: "lazy",
              width: "300",
              height: "300"
            }
          ) : null }),
          /* @__PURE__ */ jsxs("div", { class: "product-card__container", children: [
            /* @__PURE__ */ jsxs("div", { class: "product-card__content", children: [
              /* @__PURE__ */ jsx("h3", { class: "product-card__title label-m-alternate", children: /* @__PURE__ */ jsx(
                "a",
                {
                  class: "product-card__stretched-link",
                  href: url,
                  "data-algolia-click": true,
                  children: title
                }
              ) }),
              subtitle ? /* @__PURE__ */ jsx("p", { class: "product-card__subtitle body-m-light color-secondary", children: subtitle }) : null,
              price ? /* @__PURE__ */ jsx("div", { class: "product-card__price-container", children: /* @__PURE__ */ jsx("span", { class: "product-card__price label-m-regular", children: price }) }) : null
            ] }),
            /* @__PURE__ */ jsx("div", { class: "product-card__button", children: /* @__PURE__ */ jsx(
              ProductForm,
              {
                contenanceLabel,
                hit,
                isCartPage,
                resolvedGift,
                variantId
              }
            ) })
          ] })
        ]
      }
    )
  );
}

// src/sections/algolia-header-search/components/Panel.tsx
import { Fragment, jsx as jsx2, jsxs as jsxs2 } from "preact/jsx-runtime";
function PanelRecentSearches({
  iconMarkup,
  items
}) {
  if (!items.length) return null;
  return /* @__PURE__ */ jsx2("div", { class: "header-search__recent-searches", children: /* @__PURE__ */ jsx2(RecentSearches, { iconMarkup, items }) });
}
function Suggestion({ title, query }) {
  const lowerQuery = query.trim().toLowerCase();
  const index = lowerQuery ? title.toLowerCase().indexOf(lowerQuery) : -1;
  if (index === -1) return /* @__PURE__ */ jsx2(Fragment, { children: title });
  const matchEnd = index + lowerQuery.length;
  const remaining = title.slice(matchEnd);
  return remaining ? /* @__PURE__ */ jsxs2(Fragment, { children: [
    title.slice(0, matchEnd),
    /* @__PURE__ */ jsx2("em", { children: remaining })
  ] }) : /* @__PURE__ */ jsx2(Fragment, { children: title });
}
function Suggestions({
  autocomplete,
  collection,
  query
}) {
  const { items, source } = collection;
  if (!items.length) return null;
  return /* @__PURE__ */ jsx2("ul", { class: "predictive-queries", ...autocomplete.getListProps({ source }), children: items.slice(0, MAX_PREDICTIVE_QUERIES).map((hit) => {
    const suggestionQuery = String(hit.query ?? hit.objectID ?? "");
    const title = hit.title ?? suggestionQuery;
    return /* @__PURE__ */ jsx2(
      "li",
      {
        ...autocomplete.getItemProps({ item: hit, source }),
        children: /* @__PURE__ */ jsx2("a", { href: searchUrlFor(title), tabIndex: -1, children: /* @__PURE__ */ jsx2("p", { class: "body-m-light", "aria-label": suggestionQuery, children: /* @__PURE__ */ jsx2(Suggestion, { title, query }) }) })
      },
      suggestionQuery
    );
  }) });
}
function Products({
  autocomplete,
  collection,
  config,
  gifts,
  nbHits,
  query
}) {
  const { items, source } = collection;
  return /* @__PURE__ */ jsxs2("div", { class: "header-search__product-result is-open", children: [
    /* @__PURE__ */ jsxs2("div", { class: "result-title", children: [
      /* @__PURE__ */ jsxs2("span", { class: "label-m-alternate", children: [
        config.labels.products,
        " (",
        nbHits,
        ")"
      ] }),
      /* @__PURE__ */ jsx2(
        "a",
        {
          href: `${shopifyRoot()}search?q=${encodeURIComponent(query)}&type=product`,
          class: "link v-secondary m-0 w-fit",
          children: /* @__PURE__ */ jsx2("span", { class: "cta--label", children: /* @__PURE__ */ jsx2("span", { class: "cta--label__text", children: config.labels.seeAll }) })
        }
      )
    ] }),
    /* @__PURE__ */ jsx2("div", { class: "container-result", ...autocomplete.getListProps({ source }), children: items.map((hit, index) => /* @__PURE__ */ jsx2(
      ProductCard,
      {
        hit,
        itemProps: autocomplete.getItemProps({ item: hit, source }),
        resolvedGift: hit.handle ? gifts?.get(hit.handle) : null,
        contenanceLabel: config.contenanceLabel,
        isCartPage: config.isCartPage
      },
      String(hit.objectID ?? index)
    )) })
  ] });
}
function sectionLabel(hit) {
  return hit.meta?.custom?.front_display_title?.trim() || hit.title || "";
}
function Sections({
  autocomplete,
  collection,
  label
}) {
  const { items, source } = collection;
  if (!items.length) return null;
  return /* @__PURE__ */ jsxs2("div", { class: "algolia-sidebar__section", children: [
    /* @__PURE__ */ jsx2("span", { class: "label-m-alternate", children: label }),
    /* @__PURE__ */ jsx2(
      "ul",
      {
        class: "algolia-sidebar__list",
        ...autocomplete.getListProps({ source }),
        children: items.map((hit, index) => /* @__PURE__ */ jsx2(
          "li",
          {
            ...autocomplete.getItemProps({ item: hit, source }),
            dangerouslySetInnerHTML: {
              __html: `<a href='${escapeHtml(itemUrl2(hit, "collection"))}' class='algolia-sidebar__link' data-algolia-click>
                  <span class='label-m-regular'>${escapeHtml(sectionLabel(hit))}</span>
                  <span><svg xmlns='http://www.w3.org/2000/svg' width='3' height='5' viewBox='0 0 3 5' fill='none'><path d='M3 2.25L0 0V4.5L3 2.25Z' fill='black'/></svg></span>
                </a>`
            }
          },
          String(hit.objectID ?? index)
        ))
      }
    )
  ] });
}
function Explore({
  label,
  userData,
  resolved
}) {
  const html = renderRulePushIconics(userData, "algolia-push-rule") + resolved.map(
    (push, index) => renderRulePushIconic(push, {
      idPrefix: `algolia-push-resolved-${index}`
    })
  ).join("");
  if (!html) return null;
  return /* @__PURE__ */ jsxs2("div", { class: "block-push-iconics", children: [
    /* @__PURE__ */ jsx2("span", { class: "block-push-iconics__title label-m-alternate", children: label }),
    /* @__PURE__ */ jsx2("div", { class: "inside remove-scroll-bar", children: /* @__PURE__ */ jsx2(
      "div",
      {
        class: "block-push-iconics__container",
        dangerouslySetInnerHTML: { __html: html }
      }
    ) })
  ] });
}
function Panel({
  autocomplete,
  config,
  explorePushes,
  gifts,
  products,
  query,
  recentSearches,
  sections,
  suggestions,
  ...rest
}) {
  const panelProps = autocomplete.getPanelProps();
  if (!products.items.length) {
    return /* @__PURE__ */ jsx2("div", { "data-algolia-results": "", ...panelProps, children: /* @__PURE__ */ jsxs2("div", { class: "algolia-results__main", children: [
      /* @__PURE__ */ jsx2(PanelRecentSearches, { ...recentSearches }),
      /* @__PURE__ */ jsx2("div", { class: "header-search__product-result is-no-result", children: /* @__PURE__ */ jsx2("div", { children: /* @__PURE__ */ jsx2("span", { class: "body-s-light", children: config.labels.noResults }) }) })
    ] }) });
  }
  return /* @__PURE__ */ jsxs2("div", { "data-algolia-results": "", ...panelProps, children: [
    /* @__PURE__ */ jsxs2("div", { class: "algolia-results__main", children: [
      /* @__PURE__ */ jsx2(PanelRecentSearches, { ...recentSearches }),
      /* @__PURE__ */ jsx2("div", { "data-algolia-queries": true, children: /* @__PURE__ */ jsx2(
        Suggestions,
        {
          autocomplete,
          collection: suggestions,
          query
        }
      ) }),
      /* @__PURE__ */ jsx2("div", { "data-algolia-products": true, children: /* @__PURE__ */ jsx2(
        Products,
        {
          autocomplete,
          collection: products,
          config,
          gifts,
          nbHits: rest.nbHits,
          query
        }
      ) })
    ] }),
    /* @__PURE__ */ jsx2("div", { class: "algolia-results__border" }),
    /* @__PURE__ */ jsxs2("div", { class: "algolia-results__sidebar", children: [
      /* @__PURE__ */ jsx2("div", { "data-algolia-explore": true, children: /* @__PURE__ */ jsx2(
        Explore,
        {
          label: config.labels.explore,
          userData: rest.productsUserData,
          resolved: explorePushes
        }
      ) }),
      /* @__PURE__ */ jsx2("div", { "data-algolia-sections": true, children: /* @__PURE__ */ jsx2(
        Sections,
        {
          autocomplete,
          collection: sections,
          label: config.labels.sections
        }
      ) })
    ] })
  ] });
}

// src/sections/algolia-header-search/components/SearchForm.tsx
import { jsx as jsx3, jsxs as jsxs3 } from "preact/jsx-runtime";
function SearchForm({
  autocomplete,
  formRef,
  iconMarkup,
  inputRef,
  inputElement,
  placeholder
}) {
  const formProps = autocomplete.getFormProps({ inputElement });
  const { onChange, ...inputProps } = autocomplete.getInputProps({
    inputElement
  });
  const labelProps = autocomplete.getLabelProps();
  return /* @__PURE__ */ jsxs3(
    "form",
    {
      ...formProps,
      ref: formRef,
      class: "header-search__form-search",
      role: "search",
      children: [
        /* @__PURE__ */ jsx3("label", { ...labelProps, class: "header-search__label", children: placeholder || "Search" }),
        /* @__PURE__ */ jsx3(
          "button",
          {
            type: "submit",
            class: "header-search__icon-search",
            "aria-label": "Search",
            dangerouslySetInnerHTML: { __html: iconMarkup }
          }
        ),
        /* @__PURE__ */ jsx3(
          "input",
          {
            ...inputProps,
            onInput: onChange,
            ref: inputRef,
            type: "search",
            class: "header-search__input",
            placeholder,
            "aria-label": "Search",
            autocapitalize: "off",
            autocorrect: "off",
            spellcheck: false,
            maxLength: 128
          }
        )
      ]
    }
  );
}

// src/sections/algolia-header-search/components/App.tsx
import { jsx as jsx4, jsxs as jsxs4 } from "preact/jsx-runtime";
function renderSearchForm(container, props) {
  render(/* @__PURE__ */ jsx4(SearchForm, { ...props }), container);
}
function renderPanel(container, props) {
  render(/* @__PURE__ */ jsx4(Panel, { ...props }), container);
}
function clearPanel(container) {
  render(null, container);
}
function RecentSearches({
  iconMarkup,
  items
}) {
  if (!items.length) return null;
  return /* @__PURE__ */ jsx4("ul", { class: "header-search__recent-list", children: items.map((item) => /* @__PURE__ */ jsx4("li", { children: /* @__PURE__ */ jsxs4("a", { href: searchUrlFor2(item), class: "label-m-light", children: [
    /* @__PURE__ */ jsx4(
      "span",
      {
        class: "header-search__recent-icon",
        "aria-hidden": "true",
        dangerouslySetInnerHTML: { __html: iconMarkup }
      }
    ),
    /* @__PURE__ */ jsx4("span", { children: item })
  ] }) }, item)) });
}
function visibleRecentSearches(stored, query = "") {
  const normalized = query.trim().toLowerCase();
  return stored.filter((item) => !normalized || !item.toLowerCase().startsWith(normalized)).slice(0, MAX_RECENT_SEARCHES);
}
function renderRecentSearches(container, iconMarkup, stored, query = "") {
  const items = visibleRecentSearches(stored, query);
  render(/* @__PURE__ */ jsx4(RecentSearches, { iconMarkup, items }), container);
  container.hidden = items.length === 0;
  return items.length > 0;
}

// src/assets/algolia-header-panel.ts
function readHeaderSearchConfig(el) {
  const {
    algoliaAppId,
    algoliaApiKey,
    algoliaIndexProducts = "products",
    algoliaIndexSections = "sections",
    algoliaIndexSuggestions = "",
    algoliaShortQueries,
    contenanceLabel = "Contenance",
    isCartPage,
    labelExplore = "Explore",
    labelNoResults = "",
    labelProducts = "Products",
    labelSeeAll = "See all",
    labelSections = "Sections",
    searchPlaceholder = ""
  } = el.dataset;
  if (!algoliaAppId || !algoliaApiKey) return null;
  return {
    apiKey: algoliaApiKey,
    appId: algoliaAppId,
    contenanceLabel,
    isCartPage: isCartPage === "true",
    indexes: {
      products: algoliaIndexProducts,
      sections: algoliaIndexSections,
      suggestions: algoliaIndexSuggestions
    },
    // English fallbacks only — a shop whose locale file lost the key still gets
    // readable chrome instead of a blank heading. The canonical copy lives in
    // `locales/`, not here.
    labels: {
      explore: labelExplore,
      noResults: labelNoResults,
      products: labelProducts,
      seeAll: labelSeeAll,
      sections: labelSections
    },
    placeholder: searchPlaceholder,
    shortQueries: parseShortQueries(algoliaShortQueries),
    storefront: readStorefrontConfig(el)
  };
}
function readRecentSearches() {
  try {
    const rawItems = JSON.parse(
      window.localStorage.getItem(RECENT_SEARCHES_KEY) ?? "[]"
    );
    if (!Array.isArray(rawItems)) return [];
    return rawItems.filter((item) => typeof item === "string").map((item) => item.trim()).filter(Boolean).slice(0, MAX_RECENT_SEARCHES);
  } catch {
    return [];
  }
}
function writeRecentSearch(query) {
  const normalizedQuery = query.trim();
  if (!normalizedQuery) return;
  const nextItems = [
    normalizedQuery,
    ...readRecentSearches().filter(
      (item) => item.toLowerCase() !== normalizedQuery.toLowerCase()
    )
  ].slice(0, MAX_RECENT_SEARCHES);
  try {
    window.localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(nextItems));
  } catch {
  }
}
var autocompleteCorePromise = null;
function loadAutocompleteCore() {
  if (!autocompleteCorePromise) {
    autocompleteCorePromise = loadScript(
      CDN_AUTOCOMPLETE_CORE,
      SRI_AUTOCOMPLETE_CORE
    ).then(() => {
      const lib = window["@algolia/autocomplete-core"];
      if (!lib) throw new Error("Algolia autocomplete-core is not available");
      return lib;
    });
  }
  return autocompleteCorePromise;
}
var searchClientPromise = null;
var searchClientKey = "";
function loadSearchClient(appId, apiKey) {
  const key = `${appId}:${apiKey}`;
  if (!searchClientPromise || searchClientKey !== key) {
    searchClientKey = key;
    searchClientPromise = loadScript(CDN_ALGOLIASEARCH, SRI_ALGOLIASEARCH).then(
      () => {
        const lib = window["algoliasearch/lite"];
        if (!lib) throw new Error("Algolia lite client is not available");
        return lib.liteClient(appId, apiKey);
      }
    );
  }
  return searchClientPromise;
}
var debounceTimer = null;
var debounceResolvers = [];
function waitForDebounce() {
  return new Promise((resolve) => {
    debounceResolvers.push(resolve);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      const resolvers = debounceResolvers;
      debounceResolvers = [];
      resolvers.forEach((fn) => fn());
    }, SEARCH_DEBOUNCE_MS);
  });
}
function navigateTo(url) {
  if (!url || url === "#") return;
  let target;
  try {
    target = new URL(url, window.location.origin);
  } catch {
    return;
  }
  if (target.protocol !== "https:" && target.protocol !== "http:") return;
  if (target.origin !== window.location.origin) return;
  window.location.assign(target.href);
}
function collectionFor(state, sourceId) {
  return state.collections.find((c) => c.source.sourceId === sourceId) ?? {
    source: { sourceId },
    items: []
  };
}
function syncAttributes(el, props) {
  for (const [name, value] of Object.entries(props)) {
    if (typeof value === "function") continue;
    if (value === void 0 || value === null) {
      if (el.hasAttribute(name)) el.removeAttribute(name);
      continue;
    }
    const next = String(value);
    if (el.getAttribute(name) !== next) el.setAttribute(name, next);
  }
}
function exploreKeyOf(handles) {
  return [...handles].sort().join("|");
}
var EMPTY_STATE = {
  collections: [],
  completion: null,
  context: {},
  isOpen: false,
  query: "",
  status: "idle"
};
var HeaderSearchPanel = class {
  constructor(host) {
    this.host = host;
    this.autocomplete = null;
    this.config = null;
    this.root = null;
    this.panel = null;
    this.emptyState = null;
    this.recentSearches = null;
    this.iconMarkup = "";
    this.searchIconMarkup = "";
    this.exploreToken = 0;
    this.explorePushes = [];
    this.resolvedGifts = null;
    this.giftToken = 0;
    this.cartGiftToken = 0;
    /** Numéro d'état du panier avec lequel le panneau a été peint. */
    this.renderedGiftVersion = -1;
    /**
     * Pushes already resolved, keyed by the handles of the rules that fired. It
     * is what stops the render → hydrate → render cycle *and* what makes the
     * Explore column survive a query being erased then retyped: the state it
     * renders from is derived from this cache at every render, never carried over
     * from the previous one. A single "handles already sent" flag looked
     * equivalent but was not — it stayed set while `explorePushes` was cleared,
     * so the second `clean` skipped resolution and rendered nothing.
     */
    this.exploreCache = /* @__PURE__ */ new Map();
    /**
     * Redirect fired by each query's products response, keyed by the query text
     * as sent. Fed from `runBatch` rather than from the source's `getItems`: on
     * Enter, autocomplete-core cancels its pending requests before calling
     * `onSubmit`, so a query submitted inside the debounce never reaches
     * `setContext` — but its batch still runs, and the submit waits on it here.
     */
    this.redirects = /* @__PURE__ */ new Map();
    this.state = EMPTY_STATE;
    this.formEl = null;
    this.inputEl = null;
    this.config = readHeaderSearchConfig(host);
    this.root = host.querySelector("[data-autocomplete-root]");
    this.panel = host.querySelector("[data-algolia-panel]");
    this.emptyState = host.querySelector("[data-empty-state]");
    this.recentSearches = host.querySelector("[data-recent-searches]");
    this.iconMarkup = host.querySelector("template[data-recent-search-icon]")?.innerHTML.trim() ?? "";
    this.searchIconMarkup = host.querySelector("template[data-search-icon]")?.innerHTML.trim() ?? "";
  }
  /** Called by the shell on every open; the heavy setup runs only once. */
  open() {
    return this.ensureAutocomplete();
  }
  showEmptyState() {
    this.host.dataset.state = "empty";
    this.emptyState?.removeAttribute("hidden");
  }
  showResultsState(hasResults) {
    this.host.dataset.state = hasResults ? "results" : "no-results";
    this.emptyState?.setAttribute("hidden", "");
  }
  buildGetSources(config) {
    return ({
      query,
      setContext
    }) => {
      const minLength = minimumQueryLength(query, config.shortQueries);
      if (!query || query.length < minLength) return [];
      const buildQuery = (indexName, hitsPerPage, attributesToRetrieve, extraParams = {}) => {
        const { ruleContexts = [], ...restParams } = extraParams;
        return {
          indexName,
          params: {
            query,
            hitsPerPage,
            attributesToHighlight: [],
            attributesToRetrieve,
            analyticsTags: ["header-search"],
            // Appliqué aux trois requêtes, pas seulement aux produits : ici les
            // trois réglages d'index (produits, sections, suggestions) peuvent
            // pointer vers le même index produits, et sur un index qui ignore
            // l'attribut la forme négative ne filtre rien.
            filters: ALGOLIA_HIDDEN_PRODUCTS_FILTER,
            ruleContexts: [...ruleContexts, ALGOLIA_HIDDEN_GUARD_CONTEXT],
            ...restParams
          }
        };
      };
      const queries = [
        // Only the products query carries the `explore` rule context (cf.
        // PRODUCTS_RULE_CONTEXTS). Purely additive — rules without a context
        // condition keep firing exactly as before.
        buildQuery(
          config.indexes.products,
          5,
          productAttributesToRetrieve(PRODUCT_ATTRIBUTES_TO_RETRIEVE),
          { ruleContexts: PRODUCTS_RULE_CONTEXTS }
        ),
        buildQuery(config.indexes.sections, 4, SIDEBAR_ATTRIBUTES_TO_RETRIEVE)
      ];
      let suggestionsPosition = -1;
      if (config.indexes.suggestions) {
        suggestionsPosition = queries.length;
        queries.push(
          buildQuery(
            config.indexes.suggestions,
            MAX_PREDICTIVE_QUERIES,
            QUERY_ATTRIBUTES_TO_RETRIEVE
          )
        );
      }
      let batch = null;
      const runBatch = () => {
        if (!batch) {
          batch = (async () => {
            await waitForDebounce();
            const searchClient = await loadSearchClient(
              config.appId,
              config.apiKey
            );
            const { results } = await searchClient.search(queries);
            return results;
          })();
          this.rememberRedirect(
            query,
            batch.then(
              (results) => safeRedirectUrl(readRedirectUrl(results[0]))
            )
          );
        }
        return batch;
      };
      const itemsAt = async (position, onResult) => {
        const results = await runBatch();
        const result = results[position];
        onResult?.(result);
        return (result?.hits ?? []).map((hit, index) => ({
          ...hit,
          __position: index + 1
        }));
      };
      const sources = [];
      if (suggestionsPosition >= 0) {
        sources.push({
          sourceId: "suggestions",
          getItemUrl: ({ item }) => searchUrlFor3(String(item.title ?? item.query ?? "")),
          getItems: () => itemsAt(suggestionsPosition),
          onSelect: ({ item }) => navigateTo(searchUrlFor3(String(item.title ?? item.query ?? "")))
        });
      }
      sources.push(
        {
          sourceId: "products",
          getItemUrl: ({ item }) => String(item.url ?? ""),
          getItems: () => itemsAt(
            0,
            (result) => setContext({
              productsNbHits: result?.nbHits,
              productsUserData: result?.userData
            })
          ),
          onSelect: ({ event, item, setIsOpen }) => {
            const target = event.target;
            if (target instanceof Element && target.closest(
              "[data-algolia-convert], [data-product-form], [data-algolia-click]"
            )) {
              setIsOpen(true);
              return;
            }
            navigateTo(String(item.url ?? ""));
          }
        },
        {
          sourceId: "sections",
          // Same helper as the rendered `<a href>` in `Panel.tsx`, same fallback:
          // reading `item.url` alone made Enter a silent no-op on records that
          // carry none — every hit of the collections index (TP2-1784).
          getItemUrl: ({ item }) => itemUrl3(item, "collection"),
          getItems: () => itemsAt(1),
          onSelect: ({ item }) => navigateTo(itemUrl3(item, "collection"))
        }
      );
      return sources;
    };
  }
  async ensureAutocomplete() {
    if (this.autocomplete || !this.config || !this.root) return;
    const config = this.config;
    const core = await loadAutocompleteCore();
    const { createAutocomplete } = core;
    this.autocomplete = createAutocomplete({
      id: "algolia-header-search",
      placeholder: config.placeholder,
      openOnFocus: true,
      // No item is preselected: the previous implementation navigated only on an
      // explicit click or Enter, and highlighting the first hit by default would
      // change that.
      defaultActiveItemId: null,
      getSources: this.buildGetSources(config),
      navigator: {
        navigate: ({ itemUrl: itemUrl4 }) => navigateTo(itemUrl4)
      },
      onStateChange: ({ state }) => {
        this.state = state;
        this.renderAll();
      },
      onSubmit: ({ state }) => {
        writeRecentSearch(state.query);
        void this.submitQuery(config, state.query);
      }
    });
    this.renderAll();
    this.inputEl?.focus();
  }
  /**
   * Follows the Query Rule redirect of `query` when it has one, and goes to the
   * search page otherwise. Below the minimum length no search ran, so there is
   * nothing to wait for.
   */
  async submitQuery(config, query) {
    const minLength = minimumQueryLength(query, config.shortQueries);
    const redirect = query && query.length >= minLength ? await withTimeout(
      this.redirectFor(config, query),
      REDIRECT_LOOKUP_TIMEOUT_MS,
      ""
    ) : "";
    if (redirect) {
      window.location.assign(redirect);
      return;
    }
    navigateTo(searchUrlFor3(query));
  }
  /**
   * The redirect of `query`: the one its search batch already carries, or a
   * lookup of its own when no batch ever ran for it — a query pasted then
   * submitted, or a panel opened on a prefilled input.
   */
  redirectFor(config, query) {
    const known = this.redirects.get(query);
    if (known) return known;
    const lookup = loadSearchClient(config.appId, config.apiKey).then(
      (client) => lookupRedirect(client, {
        filters: ALGOLIA_HIDDEN_PRODUCTS_FILTER,
        indexName: config.indexes.products,
        query,
        ruleContexts: [
          ...PRODUCTS_RULE_CONTEXTS,
          ALGOLIA_HIDDEN_GUARD_CONTEXT
        ]
      })
    );
    this.rememberRedirect(query, lookup);
    return lookup;
  }
  rememberRedirect(query, redirect) {
    this.redirects.delete(query);
    this.redirects.set(
      query,
      redirect.catch(() => "")
    );
    if (this.redirects.size > MAX_REMEMBERED_REDIRECTS) {
      const oldest = this.redirects.keys().next().value;
      if (oldest !== void 0) this.redirects.delete(oldest);
    }
  }
  renderAll() {
    const config = this.config;
    const autocomplete = this.autocomplete;
    if (!config || !autocomplete || !this.root) return;
    syncAttributes(this.root, autocomplete.getRootProps());
    renderSearchForm(this.root, {
      autocomplete,
      formRef: (el) => {
        this.formEl = el;
      },
      iconMarkup: this.searchIconMarkup,
      inputElement: this.inputEl,
      inputRef: (el) => {
        this.inputEl = el;
      },
      placeholder: config.placeholder
    });
    if (this.recentSearches) {
      renderRecentSearches(
        this.recentSearches,
        this.iconMarkup,
        readRecentSearches(),
        this.state.query
      );
    }
    this.renderResults(config, autocomplete);
  }
  renderResults(config, autocomplete) {
    const panel = this.panel;
    if (!panel) return;
    const state = this.state;
    const minLength = minimumQueryLength(state.query, config.shortQueries);
    if (!state.query || state.query.length < minLength) {
      clearPanel(panel);
      this.explorePushes = [];
      this.showEmptyState();
      return;
    }
    const products = collectionFor(state, "products");
    this.showResultsState(products.items.length > 0);
    this.explorePushes = this.exploreFor(state.context.productsUserData);
    renderPanel(panel, {
      autocomplete,
      config: {
        contenanceLabel: config.contenanceLabel,
        indexes: config.indexes,
        isCartPage: config.isCartPage,
        labels: config.labels
      },
      explorePushes: this.explorePushes,
      gifts: this.resolvedGifts,
      nbHits: state.context.productsNbHits ?? products.items.length,
      products,
      productsUserData: state.context.productsUserData,
      query: state.query,
      // Same list as the empty state's, through the same filter — the panel just
      // owns its own mount point, inside the results grid.
      recentSearches: {
        iconMarkup: this.iconMarkup,
        items: visibleRecentSearches(readRecentSearches(), state.query)
      },
      sections: collectionFor(state, "sections"),
      suggestions: collectionFor(state, "suggestions")
    });
    void this.hydrateExplore(state.context.productsUserData);
    void this.hydrateGifts(products.items);
    void this.hydrateCartGifts();
  }
  /**
   * Miroir de `hydrateGifts`, sur l'état du panier. La comparaison porte sur le
   * numéro d'état et non sur un booléen rendu par le rafraîchissement : plusieurs
   * frappes enchaînent plusieurs rendus dont les hydratations attendent la même
   * requête, et un booléen n'aurait été lu que par la première à résoudre — celle
   * que le token fait taire quand une frappe plus récente l'a périmée.
   *
   * Le numéro est enregistré AVANT de re-rendre : le rendu rappelle cette
   * méthode, qui trouve alors l'égalité et s'arrête.
   */
  async hydrateCartGifts() {
    const token = this.cartGiftToken + 1;
    this.cartGiftToken = token;
    await refreshGiftedHandles();
    if (this.cartGiftToken !== token) return;
    if (giftedVersion() === this.renderedGiftVersion) return;
    this.renderedGiftVersion = giftedVersion();
    if (this.config && this.autocomplete) {
      this.renderResults(this.config, this.autocomplete);
    }
  }
  /**
   * Resolves the gifts that are only known by their parent GID and folds them
   * into the render. They used to be appended to each form with
   * `insertAdjacentHTML`; a Preact-owned form drops nodes it does not own, so
   * they now travel as data and the inputs come from the vdom.
   */
  async hydrateGifts(hits) {
    const token = this.giftToken + 1;
    this.giftToken = token;
    const gifts = await resolveGiftsForHits(hits, this.resolvedGifts);
    if (this.giftToken !== token || !gifts) return;
    this.resolvedGifts = gifts;
    if (this.config && this.autocomplete) {
      this.renderResults(this.config, this.autocomplete);
    }
  }
  /**
   * Re-renders the Explore column once the metaobjects referenced by the rules
   * are resolved. Fire-and-forget: the panel is already usable without it, and
   * a missing token / failing request simply leaves the inline pushes in place.
   */
  async hydrateExplore(userData) {
    const storefront = this.config?.storefront;
    const handles = readPushHandles(userData, EXPLORE_PUSH_KEY);
    if (!storefront || !handles.length) return;
    const handlesKey = exploreKeyOf(handles);
    if (this.exploreCache.has(handlesKey)) return;
    this.exploreCache.set(handlesKey, []);
    const token = this.exploreToken + 1;
    this.exploreToken = token;
    const pushes = await resolveExplorePushes(storefront, userData);
    this.exploreCache.set(handlesKey, pushes);
    if (this.exploreToken !== token || !pushes.length) return;
    if (this.config && this.autocomplete) {
      this.renderResults(this.config, this.autocomplete);
    }
  }
  /** Pushes already resolved for the rules this query fired, if any. */
  exploreFor(userData) {
    const handles = readPushHandles(userData, EXPLORE_PUSH_KEY);
    if (!handles.length) return [];
    return this.exploreCache.get(exploreKeyOf(handles)) ?? [];
  }
};
var panels = /* @__PURE__ */ new WeakMap();
function mountHeaderPanel(host) {
  let panel = panels.get(host);
  if (!panel) {
    panel = new HeaderSearchPanel(host);
    panels.set(host, panel);
  }
  return panel.open();
}
export {
  mountHeaderPanel
};
